import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FolderOpen, X, ExternalLink, BookMarked } from "lucide-react";
import { useSettings } from "@/hooks/useSettings";
import { DRIVE_GROUPS } from "@/data/content";

const TONES = ["teal", "violet", "gold", "berry"];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const placeholderLink = (group) => `https://drive.google.com/drive/folders/PLACEHOLDER-${slug(group)}`;

export const NotesFab = () => {
  const [open, setOpen] = useState(false);
  const { driveLinks = {} } = useSettings();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <motion.button
        data-testid="notes-fab"
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open class-wise notes"
        aria-expanded={open}
        initial={{ opacity: 0, scale: 0.6, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.35, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-[10.5rem] right-4 z-[70] flex items-center gap-2 rounded-full bg-white p-1.5 shadow-[0_18px_40px_-14px_rgba(14,115,104,0.7)] ring-1 ring-terra/30 lg:bottom-24 lg:right-6 lg:pr-4"
      >
        <span className="tone-badge tone-teal flex h-12 w-12 items-center justify-center rounded-full shadow"><FolderOpen className="h-6 w-6" strokeWidth={2} /></span>
        <span className="hidden text-sm font-bold text-forest lg:block">Notes</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="notes-panel-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[110] flex items-end justify-center bg-ink/50 p-4 backdrop-blur-sm sm:items-center"
            onClick={() => setOpen(false)}
          >
            <motion.div
              data-testid="notes-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="notes-panel-title"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="tone-card tone-teal relative w-full max-w-lg rounded-[2rem] p-6 shadow-layered sm:p-8"
            >
              <button data-testid="notes-panel-close" onClick={() => setOpen(false)} aria-label="Close" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-forest shadow transition-colors hover:bg-terra hover:text-white"><X className="h-5 w-5" /></button>
              <span className="tone-badge flex h-12 w-12 items-center justify-center rounded-2xl shadow-md"><BookMarked className="h-6 w-6" strokeWidth={1.8} /></span>
              <p className="eyebrow mt-5 !text-sm">Notes & Study Material</p>
              <h3 id="notes-panel-title" className="mt-1 font-display text-3xl font-bold tracking-tight text-forest">Pick your class</h3>
              <p className="mt-1 text-sm text-charcoal/70">Tap a class to open its Google Drive folder directly.</p>
              <div className="mt-5 grid max-h-[55vh] grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                {DRIVE_GROUPS.map((g, i) => {
                  const url = (driveLinks[g.group] || "").trim() || placeholderLink(g.group);
                  return (
                    <a
                      key={g.group}
                      data-testid={`notes-link-${slug(g.group)}`}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`tone-card tone-${TONES[i % 4]} group flex items-center justify-between gap-3 rounded-2xl px-4 py-3 transition-transform duration-200 hover:-translate-y-0.5`}
                    >
                      <span>
                        <span className="block font-display text-lg font-bold leading-tight text-forest">{g.group}</span>
                        <span className="tone-text block text-[10px] font-bold uppercase tracking-[0.18em]">Google Drive · {g.kind}</span>
                      </span>
                      <ExternalLink className="tone-text h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
                    </a>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
